
<v-card class="added-to-basket-dialog" :class="{'is-dragging': dragging}" :style="sheetStyle"
        @touchstart.native="onTouchStart" @touchmove.native="onTouchMove" @touchend.native="onTouchEnd" @touchcancel.native="onTouchEnd"
        @wheel.native="onWheel" @keydown.native="onKeydown">
  <div v-if="mobile" class="sheet-handle" aria-hidden="true"><span/></div>
  <v-card-title ref="header" class="dialog-header">
    <v-icon color="primary" class="confirmation-icon" aria-hidden="true">check_circle</v-icon>
    <div class="confirmation-message">
      <h2 class="text-h6">{{ $t(upgraded ? 'PRODUCT_UPGRADED_IN_BASKET' : (localQuantity > 1 ? 'PRODUCTS_ADDED_TO_BASKET' : 'PRODUCT_ADDED_TO_BASKET')) }}</h2>
    </div>
    <v-btn ref="closeButton" icon :aria-label="$t('BUTTON_CONTINUE_SHOPPING')" @click="$emit('backToProducts')">
      <v-icon>close</v-icon>
    </v-btn>
  </v-card-title>
  <div ref="content" class="v-card__text dialog-content" tabindex="0">
    <div ref="contentBody" class="dialog-body">
    <div class="added-products" tabindex="0">
      <div v-for="(item, index) in displayedItems" :key="index" class="added-product">
        <v-img :src="itemProduct(item).imageUrl" :alt="itemProduct(item).name" contain width="72" height="72" class="product-image"/>
        <div class="product-info">
          <h3>{{ itemProduct(item).name }}</h3>
          <ProductPrice :price="unitPrice(item)" :instead-of="itemProduct(item).setPriceInsteadOf"/>
        </div>
        <div class="quantity-controls" :aria-label="$t('BASKET_HEADER_AMOUNT')">
          <span class="quantity-label">{{ $t('BASKET_HEADER_AMOUNT') }}</span>
          <v-btn icon small :disabled="busy || item.amount === 0"
                 :aria-label="`${$t('BASKET_HEADER_AMOUNT')} −1`" @click="changeQuantity(item, item.amount - 1)">
            <v-icon small>remove</v-icon>
          </v-btn>
          <input :value="item.amount" type="number" min="0" :max="maximumAmount(item)" step="1" inputmode="numeric"
                 :disabled="busy" :aria-label="$t('BASKET_HEADER_AMOUNT')"
                 @change="onQuantityInput(item, $event)" @keydown.enter="$event.target.blur()"/>
          <v-btn icon small :disabled="busy || item.amount >= maximumAmount(item)"
                 :aria-label="`${$t('BASKET_HEADER_AMOUNT')} +1`" @click="changeQuantity(item, item.amount + 1)">
            <v-icon small>add</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
    <DiscountBanner :compact="mobile" detached-details class="confirmation-discount"/>
    <section v-if="recommendation" class="recommended-product" :style="recommendationStyle"
             :aria-label="recommendation.name">
      <div class="recommendation-visual">
        <v-img :src="recommendation.imageUrl" :alt="recommendation.name" contain class="recommendation-image"/>
      </div>
      <div class="recommendation-details">
        <div v-if="hasBasketQuantity" class="recommendation-delta-headline">
          {{ recommendationDeltaHeadline }}
        </div>
        <div class="recommendation-heading">
          <h3 class="recommendation-title">{{ recommendation.name }}</h3>
          <span class="recommendation-label">{{ $t('UPSELL_RECOMMENDATION') }}</span>
        </div>
        <ul v-if="recommendationPoints.length" class="recommendation-points" tabindex="0">
          <li v-for="(point, index) in recommendationPoints" :key="index">
            <v-icon small color="primary" aria-hidden="true">add</v-icon><span>{{ point }}</span>
          </li>
        </ul>
      </div>
      <div class="recommendation-actions" :class="mobile ? 'recommendation-mobile-actions' : 'recommendation-desktop-actions'">
        <ProductPrice v-if="!hasBasketQuantity" class="recommendation-price" :price="recommendation.price"
                      :instead-of="recommendation.setPriceInsteadOf"
                      :from="recommendation.detailedConfigurationSurcharge > 0"/>
        <v-btn color="primary" class="recommendation-action" @click="configureRecommendation">
          <v-icon left small>add</v-icon>
          {{ recommendationAction }}
        </v-btn>
        <div v-if="hasBasketQuantity" class="recommendation-delta-label">{{ $t('UPSELL_DELTA_PER_ITEM') }}</div>
      </div>
    </section>
    <v-alert v-if="quantityError" type="error" dense role="alert">{{ quantityError }}</v-alert>
    <v-dialog v-model="replacementChoice" max-width="480">
      <v-card class="replacement-dialog">
        <v-card-title class="replacement-header">
          <span>{{ $t('UPSELL_REPLACE_QUANTITY_TITLE') }}</span>
          <v-btn icon :aria-label="$vuetify.lang.t('$vuetify.close')" @click="replacementChoice = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text v-if="sourceItems.length">
          <v-checkbox v-for="item in sourceItems" :key="item.position.id"
                      v-model="selectedPositionIds" :value="item.position.id"
                      :aria-label="itemProduct(item).name" hide-details>
            <template #label>
              <div class="replacement-position">
                <span>{{ itemProduct(item).name }}</span>
                <span class="replacement-quantity">×{{ item.amount }}</span>
                <div class="replacement-images">
                  <v-img v-for="image in previewImages(item)" :key="image.uniqueId" :src="image.smallUrl"
                         :alt="image.name" contain width="48" max-width="48" height="48"/>
                </div>
              </div>
            </template>
          </v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="!hasMultipleSelectedUnits" color="primary" :disabled="!selectedSourceItems.length"
                 @click="startRecommendation('all')">{{ $t('UPSELL_UPGRADE_PRODUCT') }}</v-btn>
          <template v-else>
            <v-btn text color="primary" @click="startRecommendation('one')">{{ $t(selectedSourceItems.length > 1 ? 'UPSELL_REPLACE_EACH' : 'UPSELL_REPLACE_ONE') }}</v-btn>
            <v-btn color="primary" @click="startRecommendation('all')">{{ $t('UPSELL_REPLACE_ALL') }}</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </div>
  </div>
  <v-divider/>
  <v-card-actions ref="actions" class="dialog-actions">
    <v-btn text small class="configure-again" @click="$emit('configureAgain')">
      <v-icon left small>replay</v-icon>
      {{ $t("BUTTON_CONFIGURE_AGAIN") }}
    </v-btn>
    <v-spacer class="hidden-sm-and-down"/>
    <v-btn color="grey lighten-2" to="/basket">
      <v-icon left>shopping_cart</v-icon>
      {{ $t("GO_TO_BASKET") }}
    </v-btn>
    <v-btn color="primary" class="continue-shopping" @click="$emit('backToProducts')">
      {{ $t("BUTTON_CONTINUE_SHOPPING") }}
      <v-icon right>arrow_forward</v-icon>
    </v-btn>
  </v-card-actions>
</v-card>
